import type { ReactNode } from 'react';
import { cx } from '../utils/cx.js';
import styles from './SegmentedControl.module.css';

export type SegmentedOption<T extends string = string> = { value: T; label: ReactNode };

export type SegmentedControlProps<T extends string = string> = {
  value: T;
  onChange: (v: T) => void;
  options: SegmentedOption<T>[];
  'aria-label'?: string;
  /** `tabs` — display type + meta/brunswick ink (product detail, taxonomy, etc.). */
  variant?: 'default' | 'tabs';
  /**
   * When true, hide vertical separators adjacent to the active option.
   * Default false — hiding seps makes group border look broken on hover/focus.
   */
  hideNearActiveSeparators?: boolean;
};

export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  'aria-label': ariaLabel,
  variant = 'default',
  hideNearActiveSeparators = false,
}: SegmentedControlProps<T>) {
  return (
    <div
      className={cx(styles.group, variant === 'tabs' && styles.groupTabs)}
      role="group"
      aria-label={ariaLabel}
    >
      {options.map((o, i) => (
        <span key={o.value} style={{ display: 'contents' }}>
          {i > 0 ? (
            <span
              className={cx(
                styles.sep,
                hideNearActiveSeparators &&
                  (options[i - 1]?.value === value || o.value === value) &&
                  styles.sepNearActive,
              )}
              aria-hidden
            />
          ) : null}
          <button
            type="button"
            className={cx(styles.btn, value === o.value && styles.active)}
            onClick={() => onChange(o.value)}
            aria-pressed={value === o.value}
          >
            {o.label}
          </button>
        </span>
      ))}
    </div>
  );
}
